import { startDevRequest } from '../dev/devNetwork'
import { ApiError, type Limits } from './api'

/**
 * 관리자 API 클라이언트 — 공개 API(`lib/api.ts`)와 파일을 나눠 둔다.
 * 어느 호출이 인증을 요구하는지 파일 단위로 눈에 보이게 하려는 것이다.
 *
 * **토큰을 다루지 않는다.** 세션은 HttpOnly 쿠키라 JS가 읽을 수 없고, 브라우저가
 * 같은 오리진 요청에 자동으로 실어 보낸다. 화면 어디에도 저장하지 않는 게 요점이다.
 */

export interface AdminMe {
  loginId: string
  name: string
  roleCode: string
  permissions: Record<string, string[]>
}

export interface StatusOption {
  value: string
  label: string
}

interface ReviewFields {
  reviewStatus: string
  reviewStatusLabel: string
  allowedNext: StatusOption[]
}

export interface AdminSolutionCard extends ReviewFields {
  id: string
  title: string
  summary: string
  org: { id: string; name: string; isPublished: boolean }
  categoryCodes: string[]
  targetCodes: string[]
  resourceCodes: string[]
  createdAt: string
  updatedAt: string
}

export interface AdminProblemCard extends ReviewFields {
  id: string
  title: string
  categoryCodes: string[] | null
  targetCodes: string[]
  neededResourceCodes: string[] | null
  region: { code: string; name: string }
  urgency: string
  barrierNote: string | null
  neededResourceNote: string | null
  intakeChannel: string
  isPublished: boolean
  /** 등록 후 AI 후처리 상태(023). failed여도 등록은 성사돼 있다 — 사람 확인 대상일 뿐이다. */
  aiStatus: 'pending' | 'done' | 'failed'
  /** 사람 확인 대기(023). 내리는 건 후처리 충분성 판정 또는 관리자 편집뿐이다. */
  needsReview: boolean
  createdAt: string
}

/**
 * 시민문제 상세 — 검수·확정 화면이 쓴다.
 *
 * **`rawText`·`contact`는 있을 수도, 없을 수도 있다.** 서버가 `pii.read` 권한을 보고
 * 키 자체를 뺀다(admin-review.ts) — null이면 화면이 "원문 없음"과 "권한 없음"을
 * 구분하지 못한다(016 선례). `'rawText' in card`로 갈라 본다.
 *
 * `summary`는 공개용 비식별 요약이다 — **AI가 기본 입력하고 관리자가 덮어쓴다**(017·08-31).
 * null은 AI가 요약을 못 냈고 관리자도 아직 쓰지 않은 상태다.
 */
export interface AdminProblemCardDetail extends ReviewFields {
  id: string
  title: string
  titleIsDerived: boolean
  summary: string | null
  /** AI 추출 키워드(041) — 공개 상세에 그대로 나간다. 관리자가 확정·수정한다. 최대 5개. */
  keywords: string[]
  categoryCodes: string[] | null
  targetCodes: string[]
  targetEtcText: string | null
  neededResourceCodes: string[] | null
  region: { code: string; name: string }
  urgency: string
  impactScope: string | null
  barrierNote: string | null
  neededResourceNote: string | null
  pilotConditionNote: string | null
  desiredPeriod: string | null
  intakeChannel: string
  isPublished: boolean
  aiStatus: 'pending' | 'done' | 'failed'
  needsReview: boolean
  dataConfidence: number | null
  createdAt: string
  updatedAt: string
  rawText?: string | null
  contact?: {
    nameMasked: string
    name: string | null
    phone: string | null
    email: string | null
    retentionUntil: string
    deletedAt: string | null
    consents: string[]
    decryptable: boolean
  } | null
}

/** PATCH는 부분 수정이다 — 보낸 키만 바뀐다. summary는 null로 "미확정 되돌림"이 된다. */
export interface ProblemCardPatch {
  title?: string
  summary?: string | null
  /** 빈 배열은 «키워드 없음»이다 — null을 보내지 않는다(서버도 배열만 받는다). */
  keywords?: string[]
}

export interface AdminOrg {
  id: string
  name: string
  orgType: string
  baseRegionCode: string
  operationStatus: string
  isPublished: boolean
  cardCount: number
  createdAt: string
}

/**
 * 기업 상세 — 등록·수정 화면이 쓴다.
 *
 * **`contact*` 3필드와 `bizRegNo`는 있을 수도, 없을 수도 있다.** 서버가 `pii.read`
 * 권한을 보고 뺀다(admin-card.ts). 권한이 없을 때 `null`이 아니라 **필드 자체가 없는**
 * 이유는, null로 채우면 화면이 "비어 있다"로 읽고 그대로 저장해 실제 값을 지우기 때문이다.
 * `canReadContact`로 갈라 보고, false면 해당 입력란을 아예 그리지 않는다.
 */
export interface AdminOrgDetail {
  id: string
  name: string
  orgType: string
  certType: string | null
  foundedYear: number | null
  memberScale: string | null
  intro: string | null
  /** 미션·비전(018). 구글폼 선택 문항이라 비어 있을 수 있다 — 공개 대상이다. */
  mission: string | null
  vision: string | null
  homepageUrl: string | null
  logoUrl: string | null
  baseRegionCode: string
  operationStatus: string
  /** 번호 종류(021). 고유번호는 국세청 확인 대상이 아니라 상태가 not_applicable이 된다. */
  bizRegKind: 'biz_reg' | 'unique_no' | null
  bizRegStatus: 'not_provided' | 'pending' | 'valid' | 'not_applicable'
  bizRegCheckedAt: string | null
  isPublished: boolean
  canReadContact: boolean
  contactName?: string
  contactPhone?: string
  contactEmail?: string | null
  /** 사업자등록번호 원문(016). 정규화된 10자리 숫자거나, 미등록이면 null. */
  bizRegNo?: string | null
  /** 소재지 주소 원문(020). 행정동을 고른 근거이고 공개되지 않는다 — 연락처와 같은 권한 축. */
  baseAddress?: string | null
  createdAt: string
  updatedAt: string
  solutionCards: {
    id: string
    title: string
    summary: string
    reviewStatus: string
    reviewStatusLabel: string
  }[]
}

export interface OrgInput {
  name: string
  orgType: string
  certType: string | null
  foundedYear: number | null
  memberScale: string | null
  intro: string | null
  /** 미션·비전(018) — 선택 필드다. 보내지 않으면 손대지 않는다(부분 수정). */
  mission?: string | null
  vision?: string | null
  homepageUrl: string | null
  logoUrl: string | null
  baseRegionCode: string
  operationStatus: string
  contactName?: string
  contactPhone?: string
  contactEmail?: string | null
  /**
   * 소재지 주소 원문(020) — 연락처와 같은 pii 축이다. `pii.read`가 없는 계정은
   * 값을 못 받으므로 보내지도 않는다. 안 보내면 서버가 손대지 않는다.
   */
  baseAddress?: string | null
  /**
   * 사업자등록번호 — 원문·해시·확인 상태로 저장된다(016 — 08-14 원문 보관 전환).
   * `null` = 지워 달라, 미포함 = 손대지 않는다. ceoName·openDate는 국세청 진위확인
   * 입력 전용이라 저장되지 않는다.
   */
  bizRegNo?: string | null
  /** 번호 종류(021) — 고유번호(unique_no)는 국세청 확인을 건너뛴다. 미지정이면 사업자등록번호. */
  bizRegKind?: 'biz_reg' | 'unique_no' | null
  ceoName?: string | null
  openDate?: string | null
}

export interface AdminSolutionCardDetail {
  id: string
  org: { id: string; name: string }
  title: string
  summary: string
  problemSolved: string
  body: string
  categoryCodes: string[]
  targetCodes: string[]
  resourceCodes: string[]
  /** 폐기 축(022). 읽어 온 값을 그대로 되돌려 보내 과거 값을 지키기 위해 남긴다. */
  helpTypeCodes: string[]
  serviceAreaCodes: string[]
  serviceDongCodes: string[] | null
  costType: string
  accessCondition: string | null
  pilotAvailable: string | null
  capacityNote: string | null
  leadTimeDays: number | null
  impactNote: string | null
  tags: string[] | null
  sourceNote: string | null
  displayOrder: number | null
  reviewStatus: string
  reviewStatusLabel: string
  createdAt: string
  updatedAt: string
}

export interface SolutionCardInput {
  orgId: string
  title: string
  summary: string
  problemSolved: string
  body: string
  categoryCodes: string[]
  targetCodes: string[]
  resourceCodes: string[]
  /** 폐기 축(022). 읽어 온 값을 그대로 되돌려 보내 과거 값을 지키기 위해 남긴다. */
  helpTypeCodes: string[]
  serviceAreaCodes: string[]
  serviceDongCodes: string[] | null
  costType: string
  accessCondition: string | null
  pilotAvailable: string | null
  capacityNote: string | null
  leadTimeDays: number | null
  impactNote: string | null
  tags: string[] | null
  sourceNote: string | null
  displayOrder: number | null
}

/**
 * 게시판 관리. 카드의 검수 상태머신(6종)을 쓰지 않는다 — 작성자가 곧 검수자라
 * 중간 상태가 의미 없다. `isPublished` 하나로 공개를 가른다(004_board.sql 주석).
 */
export interface AdminPost {
  id: string
  board: { key: string; label: string }
  title: string
  body: string
  org: { id: string; name: string | null } | null
  isPublished: boolean
  publishedAt: string | null
  viewCount: number
  /** 관리 화면에서만 보인다. 공개 응답에는 없다. */
  authorName: string | null
  createdAt: string
  updatedAt: string
}

export interface AdminPostImage {
  id: string
  url: string
  altText: string | null
  byteSize: number
  mimeType: string
}

export interface AdminPostDetail extends AdminPost {
  images: AdminPostImage[]
}

export interface PostInput {
  boardKey: string
  title: string
  body: string
  orgId?: string | null
}

/**
 * 매칭 — 과업지시서 §4.
 *
 * **점수 근거를 화면이 다시 계산하지 않는다.** 서버가 실행 시점에 남긴 `scoreDetail`을
 * 그대로 읽어 그린다. 화면이 재계산하면 배점을 바꾼 뒤 과거 실행을 열었을 때
 * 그때의 점수와 지금의 근거가 어긋난다.
 */
/** 공통코드 편집(admin-code.ts) — 라벨·순서만 바꾼다. */
export interface CodeGroupSummary {
  group: string
  total: number
  active: number
}

export interface CodeItem {
  code: string
  group: string
  label: string
  sortOrder: number
  isActive: boolean
  note: string | null
}

export interface CodeGroupDetail {
  group: string
  items: CodeItem[]
}

export interface WeightConfigActive {
  version: string
  gradeMidMin: number
  gradeHighMin: number
  items: { scoringItem: string; weight: number }[]
  algorithmVersion: string
}

/**
 * 배점 이력 1건. 활성 1건만 매칭에 쓰이고 나머지는 **되돌릴 대상**으로 남는다 —
 * 지난 실행 결과는 그때의 배점 사본을 따로 들고 있어 이 목록이 바뀌어도 변하지 않는다.
 */
export interface WeightConfigSummary {
  version: string
  label: string
  isActive: boolean
  memo: string | null
  gradeMidMin: number
  gradeHighMin: number
  /** 노출 임계값 — `null`이면 **필터 없음**이다(0 = "0점도 노출"과 다르다). */
  minVisibleScore: number | null
  createdAt: string
  activatedAt: string | null
  items: { scoringItem: string; weight: number }[]
}

export interface WeightConfigList {
  items: WeightConfigSummary[]
  algorithmVersion: string
  /** 항목 6종과 그 순서 — 과업지시서 §4-다-3 표 그대로다. 화면이 이 순서로 그린다. */
  scoringItems: { code: string; label: string }[]
  /**
   * §4-다-3 표의 기본 배점 합(100) — **참조값이지 저장 조건이 아니다**(v5 · 축 E).
   * 분모가 「설정된 배점의 합」이라 어떤 합이든 점수는 0~100이다. 옛 이름 `total`은
   * 화면이 저장을 막는 근거로 쓰고 있어 함께 바꿨다.
   */
  totalReference: number
}

export interface WeightConfigDraft {
  version: string
  label: string
  memo?: string
  gradeMidMin: number
  gradeHighMin: number
  /** 비우면 보내지 않는다 — 서버에서 `null`(필터 없음)이 된다. */
  minVisibleScore?: number | null
  items: { scoringItem: string; weight: number }[]
}

export interface ScoreItemDetail {
  code: string
  weight: number
  /**
   * `unknown`은 "정보 없음"이다. v3(재정규화 폐지)부터는 점수를 더하지 못할 뿐 분모는 100 고정 —
   * §4-4("0점도 탈락도 아니다")는 등급·협의 큐 축이 지킨다. v2 run에서는 분모에서 빠졌다.
   */
  status: 'scored' | 'unknown'
  ratio: number | null
  points: number | null
  reason: string
}

export interface ScoreDetail {
  algorithmVersion: string
  weightConfigVersion: string
  items: ScoreItemDetail[]
  /** 잰 항목의 배점 합. 100이 아니면 "확인 필요" 항목이 있었다는 뜻이다(v2에서는 재정규화 분모였다). */
  scoredWeightSum: number
  rawPointSum: number
  /** 판정하지 못한 제외조건. 왜 안 걸렀는지를 화면에도 드러낸다. */
  inapplicableExclusions: string[]
}

export interface MatchRunSummary {
  id: string
  algorithmVersion: string
  weightConfigVersion: string
  semanticApplied: boolean
  candidateCount: number
  excludedCount: number
  noMatch: boolean
  executedAt: string
  durationMs: number | null
  /** 결과 공개(025) — run 단위 승인. 재계산으로 생긴 새 run은 미공개로 시작한다. */
  resultPublished: boolean
  resultPublishedAt: string | null
}

/** 미충족 큐(R-9) — 최신 run에 등급 «높음» 생존 후보가 0건인 공개 문제카드. */
export interface MatchUnmetItem {
  problemCard: { id: string; title: string; regionCode: string | null }
  runId: string
  executedAt: string
  noMatch: boolean
  candidateCount: number
  excludedCount: number
  unmetReasonCodes: string[]
  /** 액션 ①(협의중 전이)이 이미 진행 중인 후보 수. */
  negotiatingCount: number
  /** 액션 ④(문제→해결카드 전환)의 흔적 — 있으면 다시 전환하지 않는다. */
  convertedSolutionCardId: string | null
}

export interface MatchCandidate {
  id: string
  solutionCard: { id: string; title: string }
  orgName: string
  rank: number | null
  /** 제외된 후보는 점수가 없다 — `null`은 "0점"이 아니라 "매기지 않았다"다. */
  fitScore: number | null
  fitGrade: string | null
  scoreDetail: ScoreDetail
  unknownAxisCodes: string[]
  tagOverlapRatio: number | null
  excluded: boolean
  exclusionReasonCodes: string[]
  /** 커버리지·확실성(개편안 R-5) — v2 세대 행은 null이고, null이면 칩을 그리지 않는다. */
  coverageKind: string | null
  certainty: string | null
  uncertainConditionCodes: string[]
  /** AI 무관 판정 흔적(개편안 R-3, 4단계) — model null + «AI 미판정(…)» 텍스트는 보류다. */
  rationaleText: string | null
  rationaleModel: string | null
  status: string
  statusLabel: string
  allowedNext: StatusOption[]
}

export interface MatchRunDetail extends MatchRunSummary {
  problemCard: { id: string; title: string }
  weightSnapshot: WeightConfigActive
  candidates: MatchCandidate[]
}

/**
 * dev 패널 — `DEV_PANEL_PASSWORD`가 있는 환경에만 존재하는 라우트다(admin-dev.ts).
 * 없으면 404가 정상 응답이고, 그 자리에 오는 사람은 `dev:access` 세션뿐이다.
 */
export interface DevPanelStatus {
  nodeEnv: string
  migrations: { total: number; recent: { filename: string; appliedAt: string }[] }
  counts: {
    org: number
    solutionCard: number
    problemCard: number
    post: number
    adminUser: number
    liveSession: number
  }
}

export interface DevAuditItem {
  id: string
  action: string
  entityType: string
  entityId: string | null
  before: unknown
  after: unknown
  createdAt: string
  actorLoginId: string | null
  actorName: string | null
}

interface Paged<T> {
  items: T[]
  total: number
  limit: number
  offset: number
}

/**
 * 데이터 관리(검색·필터·내려받기)가 쓰는 조건. **목록과 내려받기가 같은 값을 보낸다** —
 * 서버도 한 빌더(`lib/dataset-query.ts`)로 처리하므로 화면 건수와 파일 건수가 어긋나지 않는다.
 *
 * 값을 문자열로 두는 이유: 그대로 쿼리스트링이 되고, 빈 문자열이 곧 "조건 없음"이다.
 */
/** 해결카드 × 시민문제 후보 한 행. 서버 `GET /api/admin/solution-matches`의 item이다. */
export type AdminSolutionMatch = {
  id: string
  solutionCard: { id: string; title: string }
  org: { id: string; name: string }
  problemCard: { id: string; title: string; regionCode: string | null }
  rank: number | null
  fitScore: number | null
  fitGrade: string | null
  excluded: boolean
  exclusionReasonCodes: string[]
  status: string
  statusLabel: string
  executedAt: string
}

export type DatasetKey = 'solution-cards' | 'problem-cards' | 'orgs' | 'solution-matches'

/**
 * 기업 선택 드롭다운·기업 관리 목록이 한 번에 받는 상한.
 *
 * 목록 API 기본값(50)보다 크게 잡는다 — 이 화면들에는 페이지 이동이 없어서
 * 잘리면 **잘린 줄 모른 채** 목록에 없는 기업을 "없다"고 판단하게 된다.
 * MVP 기업 수는 15~20건(과업지시서 §6-가)이라 지금은 충분하고, 넘어가면 검색을 붙인다.
 */
export const ORG_LIST_LIMIT = 200

/** interface가 아니라 type인 이유: 쿼리스트링 빌더가 받는 `Record`에 그대로 넘길 수 있다. */
export type DatasetFilters = {
  q?: string
  from?: string
  to?: string
  status?: string
  isPublished?: string
  categoryCodes?: string
  targetCodes?: string
  resourceCodes?: string
  serviceAreaCodes?: string
  orgId?: string
  regionCode?: string
  urgency?: string
  orgType?: string
  baseRegionCode?: string
  // 해결카드 기준 매칭 역조회 전용(관리자UX N-2)
  solutionCardId?: string
  grade?: string
  minScore?: string
  includeExcluded?: string
  operationStatus?: string
  aiStatus?: string
  needsReview?: string
  limit?: number
  offset?: number
}

function datasetQs(f: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(f)) {
    if (v !== undefined && v !== '') sp.set(k, String(v))
  }
  const s = sp.toString()
  return s.length > 0 ? `?${s}` : ''
}

export interface ExportedFile {
  blob: Blob
  fileName: string
}

/**
 * 서버가 준 이름을 그대로 쓴다. 한글 파일명은 RFC 5987 `filename*`로 오므로 디코드한다 —
 * 화면에서 이름을 다시 조립하면 서버가 붙인 날짜·데이터셋과 어긋난다.
 */
function fileNameFrom(header: string | null): string {
  const star = header === null ? null : /filename\*=UTF-8''([^;]+)/i.exec(header)?.[1]
  if (star !== null && star !== undefined) {
    try {
      return decodeURIComponent(star)
    } catch {
      // 깨진 인코딩이면 아래 기본값으로 떨어진다 — 여기서 멈추면 받은 파일을 잃는다.
    }
  }
  return 'haebom-export'
}

/**
 * 파일 응답 전용. 실패는 **JSON으로 온다** — 그걸 그대로 blob으로 저장하면
 * 사용자는 열리지 않는 엑셀을 받아 들고 원인을 모른다. 여기서 오류로 바꿔 던진다.
 */
async function requestFile(path: string): Promise<ExportedFile> {
  const done = startDevRequest('GET', path)
  let res: Response
  try {
    res = await fetch(path)
  } catch (cause) {
    done(0, 'network_error')
    throw new ApiError(0, 'network_error', ['서버에 연결하지 못했습니다.'], { cause })
  }
  if (!res.ok) {
    const text = await res.text()
    const body: unknown = text.length > 0 ? JSON.parse(text) : null
    const e = body as { error?: unknown; detail?: unknown } | null
    const code = typeof e?.error === 'string' ? e.error : 'unknown_error'
    done(res.status, code)
    throw new ApiError(res.status, code, Array.isArray(e?.detail) ? e.detail.map(String) : [])
  }
  done(res.status, null)
  return { blob: await res.blob(), fileName: fileNameFrom(res.headers.get('content-disposition')) }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response
  // 본문이 없는데 content-type을 붙이면 Fastify가 "빈 본문"으로 400을 낸다.
  // 로그아웃처럼 본문 없는 POST가 여기 걸려 조용히 실패한 적이 있다.
  // FormData도 마찬가지로 손대지 않는다 — multipart는 헤더에 boundary가 들어가야 하는데
  // 그 값은 브라우저만 안다. 우리가 content-type을 덮어쓰면 서버가 본문을 못 가른다.
  const hasBody = init?.body !== undefined && init.body !== null
  const isJsonBody = hasBody && !(init.body instanceof FormData)
  const done = startDevRequest(init?.method ?? 'GET', path)
  try {
    res = await fetch(path, {
      ...init,
      ...(isJsonBody ? { headers: { 'content-type': 'application/json', ...init.headers } } : {}),
    })
  } catch (cause) {
    done(0, 'network_error')
    throw new ApiError(0, 'network_error', ['서버에 연결하지 못했습니다.'], { cause })
  }

  const text = await res.text()
  const body: unknown = text.length > 0 ? JSON.parse(text) : null

  if (!res.ok) {
    const e = body as { error?: unknown; detail?: unknown } | null
    const code = typeof e?.error === 'string' ? e.error : 'unknown_error'
    const detail = Array.isArray(e?.detail) ? e.detail.map(String) : []
    done(res.status, code)
    throw new ApiError(res.status, code, detail)
  }
  done(res.status, null)
  return body as T
}

/** AI 설정 상태 — 키는 설정 여부·출처만 온다(원문 없음). envDefault는 "되돌리면 무엇이 되나". */
export interface AiSettingsStatus {
  enabled: boolean
  keyConfigured: boolean
  keySource: 'admin' | 'env' | null
  modelConfigured: boolean
  model: string | null
  modelSource: 'admin' | 'env' | null
  envDefault: { keyConfigured: boolean; model: string | null }
}

export interface AiSettingsResult {
  enabled: boolean
  keyConfigured: boolean
  keySource: 'admin' | 'env' | null
  model: string | null
  modelSource: 'admin' | 'env' | null
}

export const adminApi = {
  login: (loginId: string, password: string) =>
    request<AdminMe>('/api/admin/login', {
      method: 'POST',
      body: JSON.stringify({ loginId, password }),
    }),

  logout: () => request<{ ok: boolean }>('/api/admin/logout', { method: 'POST' }),

  me: () => request<AdminMe>('/api/admin/me'),

  solutionCards: (f: DatasetFilters = {}) =>
    request<Paged<AdminSolutionCard>>(`/api/admin/solution-cards${datasetQs(f)}`),

  problemCards: (f: DatasetFilters = {}) =>
    request<Paged<AdminProblemCard>>(`/api/admin/problem-cards${datasetQs(f)}`),

  orgs: (f: DatasetFilters = {}) => request<Paged<AdminOrg>>(`/api/admin/orgs${datasetQs(f)}`),

  /**
   * 해결카드 기준 매칭 역조회(관리자UX N-2) — 「이 카드가 어떤 시민문제와 얼마나 맞나」.
   * 목록과 함께 `summary`(등급 분포·카드/기업 수·최고·평균)를 받는다. 목록만 주면
   * 「이 조건에 몇 건인가」를 알려고 페이지를 넘겨야 하고, 그게 이 화면을 만들게 된 불편이다.
   */
  solutionMatches: (f: DatasetFilters = {}) =>
    request<
      Paged<AdminSolutionMatch> & {
        summary: {
          total: number
          byGrade: { high: number; mid: number; low: number }
          excluded: number
          solutionCardCount: number
          orgCount: number
          maxScore: number | null
          avgScore: number | null
        }
      }
    >(`/api/admin/solution-matches${datasetQs(f)}`),

  /** 다이렉트 모드(023) — 읽기는 로그인만, 변경은 admin:manage(서버 admin-setting.ts). */
  directMode: () => request<{ enabled: boolean }>('/api/admin/settings/direct-mode'),

  setDirectMode: (enabled: boolean) =>
    request<{ enabled: boolean }>('/api/admin/settings/direct-mode', {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),

  /** 기업 자동 공개(08-25) — 기업판 다이렉트 모드. 권한 구분은 direct-mode와 같다. */
  orgAutoPublish: () => request<{ enabled: boolean }>('/api/admin/settings/org-auto-publish'),

  setOrgAutoPublish: (enabled: boolean) =>
    request<{ enabled: boolean }>('/api/admin/settings/org-auto-publish', {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),

  /**
   * 매칭 결과 자동 공개(08-27 확정 모델) — 실행은 스위치 없이 카드 공개 시 항상 자동이고,
   * 이 스위치는 그 결과의 **공개**만 가른다. 권한 구분은 direct-mode와 같다.
   */
  matchAutoPublish: () => request<{ enabled: boolean }>('/api/admin/settings/match-auto-publish'),

  setMatchAutoPublish: (enabled: boolean) =>
    request<{ enabled: boolean }>('/api/admin/settings/match-auto-publish', {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),

  /**
   * 갯수 상한(030, 사용자 지시 08-25) — 세 값을 한 번에 읽고 한 번에 저장한다.
   * 부분 저장을 받지 않으므로 화면은 **바꾸지 않은 칸도 현재값 그대로** 실어 보낸다.
   * 읽기는 로그인만, 변경은 admin:manage(서버 admin-setting.ts).
   */
  limits: () => request<{ limits: Limits; max: number }>('/api/admin/settings/limits'),

  setLimits: (limits: Limits) =>
    request<{ limits: Limits; max: number }>('/api/admin/settings/limits', {
      method: 'PUT',
      body: JSON.stringify(limits),
    }),

  /**
   * 공통코드 라벨·순서(09-03) — 화면 선택지의 이름과 차례를 배포 없이 바꾼다.
   * 저장은 **그룹 전체를 순서대로** 보낸다(배열 순서 = sort_order). 부분 저장은 400이고
   * 코드 추가·삭제·활성 토글은 아예 없다 — 코드값의 도달점은 시드다(서버 admin-code.ts).
   */
  codeGroups: () =>
    request<{ items: CodeGroupSummary[] }>('/api/admin/common-codes/groups'),

  codeGroup: (group: string) =>
    request<CodeGroupDetail>(`/api/admin/common-codes/${encodeURIComponent(group)}`),

  setCodeGroup: (group: string, items: { code: string; label: string }[]) =>
    request<CodeGroupDetail>(`/api/admin/common-codes/${encodeURIComponent(group)}`, {
      method: 'PUT',
      body: JSON.stringify({ items }),
    }),

  /**
   * AI 설정(사용자 결정 08-24) — env(SOPS)는 디폴트, 여기 저장값이 우선.
   * 읽기는 로그인만, 변경·모델 목록은 admin:manage(서버 admin-ai.ts).
   * **키 원문은 어떤 응답에도 없다** — 설정 여부·출처만 온다.
   */
  aiSettings: () => request<AiSettingsStatus>('/api/admin/ai/status'),

  putAiSettings: (body: { apiKey?: string | null; model?: string | null }) =>
    request<AiSettingsResult>('/api/admin/ai/settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  aiModels: () => request<{ models: string[]; current: string | null }>('/api/admin/ai/models'),

  aiTest: () =>
    request<{ ok: boolean; modelAvailable: boolean | null; reason: string | null }>(
      '/api/admin/ai/test',
      { method: 'POST' },
    ),

  problemCard: (id: string) =>
    request<AdminProblemCardDetail>(`/api/admin/problem-cards/${id}`),

  /** 제목·요약·키워드 확정. 제목을 고치면 서버가 `titleIsDerived`를 내려 공개 전환이 열린다(017). */
  updateProblemCard: (id: string, body: ProblemCardPatch) =>
    request<{ id: string; updatedFields: string[] }>(`/api/admin/problem-cards/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /** AI 후처리 재실행 — 등록 시점과 같은 규칙으로 빈 축을 채우고 충분성을 다시 판정한다. */
  rerunProblemAi: (id: string) =>
    request<{ aiStatus: 'done' | 'failed'; aiReason?: string; needsReview: boolean; autoPublished: boolean }>(
      `/api/admin/problem-cards/${id}/ai-rerun`,
      { method: 'POST' },
    ),

  /**
   * 내려받기. **모든 호출이 감사 기록에 남는다**(서버 `admin-export.ts`) —
   * 화면에도 그렇게 적어 두어야 운영자가 모르고 쓰는 일이 없다.
   */
  exportDataset: (
    dataset: DatasetKey,
    f: DatasetFilters,
    format: 'csv' | 'xlsx',
    includeContact = false,
  ) => {
    // 페이지네이션은 빼고 보낸다 — 내려받기는 **조건에 걸린 전부**가 대상이다.
    // 화면에 보이는 50건만 받아 놓고 전부라고 믿게 만들지 않는다.
    const params: Record<string, string | number | undefined> = {
      ...f,
      limit: undefined,
      offset: undefined,
      format,
    }
    if (includeContact) params.includeContact = 'true'
    return requestFile(`/api/admin/export/${dataset}${datasetQs(params)}`)
  },

  org: (id: string) => request<AdminOrgDetail>(`/api/admin/orgs/${id}`),

  createOrg: (body: OrgInput) =>
    request<{ id: string; isPublished: boolean; bizRegStatus: string }>('/api/admin/orgs', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateOrg: (id: string, body: Partial<OrgInput>) =>
    request<{ id: string; updatedFields: string[] }>(`/api/admin/orgs/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  solutionCard: (id: string) =>
    request<AdminSolutionCardDetail>(`/api/admin/solution-cards/${id}`),

  createSolutionCard: (body: SolutionCardInput) =>
    request<{ id: string; reviewStatus: string }>('/api/admin/solution-cards', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateSolutionCard: (id: string, body: Partial<SolutionCardInput>) =>
    request<{ id: string; updatedFields: string[] }>(`/api/admin/solution-cards/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  setReviewStatus: (kind: 'solution-cards' | 'problem-cards', id: string, reviewStatus: string) =>
    request<{ id: string } & ReviewFields>(`/api/admin/${kind}/${id}/review-status`, {
      method: 'PATCH',
      body: JSON.stringify({ reviewStatus }),
    }),

  setOrgPublished: (id: string, isPublished: boolean) =>
    request<{ id: string; isPublished: boolean }>(`/api/admin/orgs/${id}/publish`, {
      method: 'PATCH',
      body: JSON.stringify({ isPublished }),
    }),

  posts: (params: { board?: string; published?: string }) => {
    const sp = new URLSearchParams()
    if (params.board !== undefined && params.board !== '') sp.set('board', params.board)
    if (params.published !== undefined && params.published !== '')
      sp.set('published', params.published)
    const q = sp.toString()
    return request<Paged<AdminPost>>(`/api/admin/posts${q.length > 0 ? `?${q}` : ''}`)
  },

  post: (id: string) => request<AdminPostDetail>(`/api/admin/posts/${id}`),

  createPost: (body: PostInput) =>
    request<{ id: string; isPublished: boolean }>('/api/admin/posts', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updatePost: (id: string, body: Partial<PostInput>) =>
    request<{ id: string; isPublished: boolean }>(`/api/admin/posts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  setPostPublished: (id: string, isPublished: boolean) =>
    request<{ id: string; isPublished: boolean; publishedAt: string | null }>(
      `/api/admin/posts/${id}/publish`,
      { method: 'PATCH', body: JSON.stringify({ isPublished }) },
    ),

  deletePost: (id: string) =>
    request<{ id: string; deleted: boolean }>(`/api/admin/posts/${id}`, { method: 'DELETE' }),

  /** 한 번에 한 장이다 — 여러 장을 한 요청에 담으면 부분 실패를 화면이 되돌리기 어렵다. */
  uploadPostImage: (id: string, file: File, altText: string) => {
    const form = new FormData()
    form.append('altText', altText)
    // 파일은 마지막에 넣는다. 서버는 파일을 만난 시점까지 읽은 텍스트 필드만 볼 수 있다
    // (multipart는 스트림이다) — altText가 뒤에 오면 조용히 누락된다.
    form.append('file', file)
    return request<{ id: string; url: string; altText: string | null }>(
      `/api/admin/posts/${id}/images`,
      { method: 'POST', body: form },
    )
  },

  deletePostImage: (imageId: string) =>
    request<{ id: string; deleted: boolean }>(`/api/admin/posts/images/${imageId}`, {
      method: 'DELETE',
    }),

  activeWeightConfig: () => request<WeightConfigActive>('/api/admin/weight-configs/active'),

  weightConfigs: () => request<WeightConfigList>('/api/admin/weight-configs'),

  /** 만들기만 한다 — 켜는 것은 별도 동작이다(실수한 값이 곧바로 전 매칭에 걸리지 않게). */
  createWeightConfig: (body: WeightConfigDraft) =>
    request<WeightConfigSummary>('/api/admin/weight-configs', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** **되돌리기도 이 호출이다** — 옛 버전을 다시 켜는 것이 롤백이다. */
  activateWeightConfig: (version: string) =>
    request<WeightConfigSummary & { previousVersion: string | null }>(
      `/api/admin/weight-configs/${version}/activate`,
      { method: 'POST' },
    ),

  /** 실행할 때마다 새 결과가 쌓인다 — 덮어쓰지 않으므로 이전 배점의 결과와 비교할 수 있다. */
  runMatch: (problemCardId: string) =>
    request<MatchRunSummary & { problemCardId: string }>(
      `/api/admin/problem-cards/${problemCardId}/match-runs`,
      { method: 'POST' },
    ),

  matchRuns: (problemCardId: string) =>
    request<Paged<MatchRunSummary>>(`/api/admin/problem-cards/${problemCardId}/match-runs`),

  matchRun: (runId: string) => request<MatchRunDetail>(`/api/admin/match-runs/${runId}`),

  /**
   * dev 패널 조회 2종. `DEV_PANEL_PASSWORD` 없는 환경에서는 라우트 자체가 없어 404,
   * 권한(`dev:access`) 없는 세션은 403이다 — 둘 다 화면이 그대로 오류로 보여 준다.
   * (2026-08-19 `eligible`·`login`은 원클릭 폐지로 사라졌다 — 기획 §15-2)
   */
  devStatus: () => request<DevPanelStatus>('/api/admin/dev-panel/status'),

  devAudit: (limit = 50) =>
    request<{ items: DevAuditItem[] }>(`/api/admin/dev-panel/audit?limit=${limit}`),

  matchUnmet: (limit = 50) =>
    request<Paged<MatchUnmetItem>>(`/api/admin/match-unmet?limit=${limit}`),

  /** 결과 공개 승인·해제(025) — `card.publish` 권한. 해제도 같은 라우트다. */
  setMatchResultPublish: (runId: string, published: boolean) =>
    request<{ id: string; resultPublished: boolean; resultPublishedAt: string | null }>(
      `/api/admin/match-runs/${runId}/result-publish`,
      { method: 'PATCH', body: JSON.stringify({ published }) },
    ),

  setMatchStatus: (candidateId: string, status: string, reason?: string) =>
    request<{ id: string; status: string; statusLabel: string; allowedNext: StatusOption[] }>(
      `/api/admin/match-candidates/${candidateId}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify(reason === undefined ? { status } : { status, reason }),
      },
    ),
}

/** 권한 확인은 화면 편의일 뿐이다 — 실제 차단은 서버가 한다(`requireAdmin`). */
export function can(me: AdminMe | undefined, resource: string, action: string): boolean {
  return me?.permissions[resource]?.includes(action) ?? false
}
